import { Outlet, useNavigate } from "react-router-dom";
import styles from "./Layout.module.css";
import { CopyRegular } from "@fluentui/react-icons";
import { IconButton, Dialog, DialogType, Stack, TextField, IDropdownOption, Facepile, PersonaSize, IFacepilePersona, ContextualMenu, IContextualMenuItem, Shimmer, ShimmerElementType, Persona, Icon } from "@fluentui/react";
import { useContext, useEffect, useRef, useState } from "react";
import { ChangeGptModeDropdown } from "../../components/common/Dropdown";
import { AppStateContext } from "../../state/AppProvider";
import { LoginUserInfo, getCompanyIcon } from "../../api";
import { MessageDialog } from "../../components/common/Dialog";
import { useBoolean } from "@fluentui/react-hooks";
import { ErrorMessage } from "../../api";
import { useIsMobile } from "../../hooks/useIsMobile";
import Switch from "../../components/common/Switch";

interface AvatarProps {
    username: string
    isAdmin: boolean
    onLogOutClick: () => void
}
const Avatar = (props: AvatarProps) => {
    const {username, onLogOutClick, isAdmin} = props
    const faceRef = useRef(null)
    const [openMenu, setOpenMenu] = useBoolean(false)
    const onAdminClicked = () => {
        window.open('/menu', "_blank")
    }
    const outputCsv = async () => {
        window.open('/api/outputcsv', '_blank');
    }
    const menuItems: IContextualMenuItem[] = [
        {
            key: "admin_menu",
            iconProps: {iconName: "ContextMenu"},
            text: '管理者メニュー',
            onClick: onAdminClicked
        },
        {
            key: "download_csv",
            iconProps: {iconName: "Download"},
            text: '履歴をダウンロード',
            onClick: () => {
                outputCsv()
            },
        },
        {
            key: "logout",
            text: "ログアウト",
            iconProps: { iconName: "SignOut"},
            onClick: onLogOutClick
        }
    ]
    return(
        <div>
            <div ref={faceRef} style={{cursor: "pointer", userSelect: "none"}}>
                <Stack 
                    horizontal 
                    verticalAlign="center" 
                    tokens={{childrenGap: "10px"}}
                    styles={{
                        root:{
                                transition: "0.1s",
                                borderRadius: "4px",
                                padding: "4px 8px",
                                selectors: {
                                    ':hover': {
                                    background: '#e1dfdd',
                                }
                            }
                        }
                    }}
                    onClick={setOpenMenu.toggle}
                    data-testid="user-menu"
                >
                    <Persona 
                        size={PersonaSize.size40}
                        imageInitials={username.at(0)}
                        hidePersonaDetails
                    />
                    <Icon iconName="ChevronDown" styles={{ root: { fontSize: "12px" } }} />
                </Stack>
            </div>
            <ContextualMenu 
                hidden={!openMenu}
                target={faceRef}
                items={menuItems.filter(v => v.key !== "admin_menu" || isAdmin)}
                onDismiss={setOpenMenu.setFalse}
            />
        </div>
    )
}

const Layout = () => {
    const [isSharePanelOpen, setIsSharePanelOpen] = useState<boolean>(false);
    const [copyClicked, setCopyClicked] = useState<boolean>(false);
    const [copyText, setCopyText] = useState<string>("Copy URL");
    const appStateContext = useContext(AppStateContext);
    const isMobile = useIsMobile()
    const loginUserInfo = appStateContext?.state.loginUserInfo
    const [imageUrl, setImageUrl] = useState<string | undefined>();
    const [hideMessageDialog, { toggle: toggleMessageDialog }] = useBoolean(true);
    const [showCancelButton, setShowCancelButton] = useState<boolean>(false);
    const [dialogHandlers, setDialogHandlers] = useState({ handleOk: () => {}, handleCancel: () => {} });
    const [dialogMsg, setMessageDialog] = useState<ErrorMessage | null>()
    const navigate = useNavigate()

    // メッセージダイアログ
    const showDialog = async (title: string, subtitle: string, showCancelButton: boolean) => {
        setShowCancelButton(showCancelButton)
        setMessageDialog({ title, subtitle })
        toggleMessageDialog();
    
        const dialogResponse = await new Promise((resolve) => {
            const handleOk = () => {
                resolve(true)
            }
            const handleCancel = () => {
                resolve(false)
            }
            setDialogHandlers({ handleOk, handleCancel })
        });
        toggleMessageDialog();
        return dialogResponse;
    }
    
    const messageDialogContentProps = {
        type: DialogType.normal,
        title: dialogMsg?.title,
        closeButtonAriaLabel: 'Close',
        subText: dialogMsg?.subtitle,
    };

    const handleSharePanelDismiss = () => {
        setIsSharePanelOpen(false);
        setCopyClicked(false);
        setCopyText("Copy URL");
    };

    const handleCopyClick = () => {
        navigator.clipboard.writeText(window.location.href);
        setCopyClicked(true);
    };

    // RAGメニュー切替/モバイル対応
    const handleRAGMenuClick = () => {
        appStateContext?.dispatch({ type: "SET_IS_RAG_ENABLED", payload: {isEnabled: !appStateContext.state.isRAGEnabled, openRAGMenuIfToggleToOpen: true}});
        navigate("/")
    };

    // GPTモデル変更
    const handleDropdown = (e: any,values?:IDropdownOption) => {
        if (values?.key) {
            appStateContext?.dispatch({ type: 'SET_SELECTED_GPT_MODEL_INDEX', payload: values.key.toString() })
        }
    };

    // 管理者Menu
    const handleAdminMenu = () => {
        window.open('/menu', '_blank');
    };

    // ログアウト
    const logout = async () => {
        await fetch('/api/logout');
        navigate('/login/' + localStorage.getItem('WorQuforGAI_companykey'));
      };

    useEffect(() => {
        if (copyClicked) {
            setCopyText("Copied URL");
        }
    }, [copyClicked]);

    useEffect(() => {}, [appStateContext?.state.isCosmosDBAvailable.status]);

    useEffect(() => {
        const fetchImage = async () => {
            try {
              const objectURL = await getCompanyIcon();
              setImageUrl(objectURL);
            } catch (error) {
              console.error('クライアント側アイコン取得例外エラー:', error);
            }
        };
        fetchImage();
      }, []);

    // ユーザー情報取得
    useEffect(() => {
        // ユーザー情報を取得する非同期関数
        const fetchLoginUserInfo = async () => {
            try {
                const response = await fetch('/api/loginUserInfo'); // Pythonのエンドポイントにリクエストを送信
                if (!response.ok) {
                    throw new Error('Failed to fetch user info');
                }
                const data: LoginUserInfo = await response.json();
                appStateContext?.dispatch({type: "SET_LOGIN_USER_INFO", payload: data})
            } catch (error) {
                console.error(error);
            }
        };
        fetchLoginUserInfo(); 
    }, []); 

    // モバイルメニューの状態管理
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);

    const handleMobileMenuClick = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const TitleElement = () => {
        return (<Stack horizontal tokens={{childrenGap: "10px"}} verticalAlign="center">
            <IconButton 
                styles={{
                    icon: {
                        fontSize: "16pt",
                        color: "black"
                    }
                }}
                iconProps={{iconName: "CollapseMenu"}}
                data-testid="sidebar-toggle"
                onClick={() => appStateContext?.dispatch({type: "SET_SIDEBAR_OPEN", payload: !appStateContext.state.isSideBarOpen})}
            />
            <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: 120, height: 36 }]} isDataLoaded={Boolean(imageUrl)}> 
                <img
                    draggable={false}
                    src={imageUrl}
                    className={styles.headerIcon}
                    aria-hidden="true"
                    onClick={async () => {
                        const code = await fetch("/").then(r => r.status)
                        const redirect_dst = code !== 200 ? '/login/' + localStorage.getItem('WorQuforGAI_companykey') : '/'
                        navigate(redirect_dst)
                    }}
                />
            </Shimmer>
            <div className={styles.headerTitleContainer}>
                <h1 className={styles.headerTitle}>WorQu for GAI</h1>
            </div>
        </Stack>)
    }
    const RAGToggle = () => {
        return (
            <div className={styles.mobileRAGSection}>
                <span className={styles.mobileRAGLabel}>文書検索<br/>機能</span>
                <Switch
                    id="mobileRagModeSwitch"
                    checked={appStateContext?.state?.isRAGEnabled || false}
                    onChange={handleRAGMenuClick}
                />
            </div>
        )
    }

    return (
        <div className={styles.layout}>
            <MessageDialog
                hidden={hideMessageDialog}
                onDismiss={dialogHandlers.handleCancel}
                onConfirm={dialogHandlers.handleOk}
                dialogContentProps={messageDialogContentProps}
                showCancelButton={showCancelButton}
            >
            </MessageDialog>

            <header className={styles.header} role={"banner"}>
                <Stack style={{padding: isMobile ? "0px 20px 10px 20px" : "0px 20px"}}>
                    {
                        isMobile &&
                        <Stack horizontal style={{paddingRight: "16px"}} horizontalAlign="space-between" verticalAlign="center" >
                            {<TitleElement />}
                            {loginUserInfo && <Avatar username={loginUserInfo?.name} onLogOutClick={logout} isAdmin={false}/>}
                        </Stack>
                    }
                    <Stack horizontal tokens={{childrenGap:"20px"}} verticalAlign="center" horizontalAlign={isMobile ? "center" : "space-between"}>
                        {!isMobile && <TitleElement />}
                        <Stack horizontal tokens={{childrenGap:"20px"}} verticalAlign="center">
                            <ChangeGptModeDropdown onChange={(e,option?) => handleDropdown(e,option)} options={[]} disabled={false} isRAGSelected={appStateContext?.state.selectedRAGIds.size != 0} currentChat={appStateContext?.state.currentChat}/>
                            <RAGToggle />
                        </Stack>
                        {!isMobile && 
                            <Stack horizontal style={{width: "330px", maxWidth: "330px", paddingRight: "16px"}} verticalAlign="center" horizontalAlign="end">
                                {loginUserInfo && <Avatar username={loginUserInfo?.name} onLogOutClick={logout} isAdmin={loginUserInfo.authlevel >= 2}/>}
                            </Stack>
                        }
                    </Stack>
                </Stack>
            </header>
            
            <Outlet />
            <Dialog 
                onDismiss={handleSharePanelDismiss}
                hidden={!isSharePanelOpen}
                styles={{
                    
                    main: [{
                        selectors: {
                          ['@media (min-width: 480px)']: {
                            maxWidth: '600px',
                            background: "#FFFFFF",
                            boxShadow: "0px 14px 28.8px rgba(0, 0, 0, 0.24), 0px 0px 8px rgba(0, 0, 0, 0.2)",
                            borderRadius: "8px",
                            maxHeight: '200px',
                            minHeight: '100px',
                          }
                        }
                      }]
                }}
                dialogContentProps={{
                    title: "Share the web app",
                    showCloseButton: true
                }}
            >
                <Stack horizontal verticalAlign="center" style={{gap: "8px"}}>
                    <TextField className={styles.urlTextBox} defaultValue={window.location.href} readOnly/>
                    <div 
                        className={styles.copyButtonContainer} 
                        role="button" 
                        tabIndex={0} 
                        aria-label="Copy" 
                        onClick={handleCopyClick}
                        onKeyDown={e => e.key === "Enter" || e.key === " " ? handleCopyClick() : null}
                    >
                        <CopyRegular className={styles.copyButton} />
                        <span className={styles.copyButtonText}>{copyText}</span>
                    </div>
                </Stack>
            </Dialog>
        </div>
    );
};

export default Layout;